import React, { useState } from 'react';
import { submitContactForm, type ContactFormData } from '../utils/api';

const fieldClass =
  'mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-3 text-base text-ink outline-none focus:border-forest';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    teamSize: '',
    subject: '',
    message: '',
    inquiryType: 'general',
    honeypot: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formLoadTime] = useState(Date.now());

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      if (formData.honeypot) {
        setSubmitStatus('error');
        setErrorMessage('That submission was blocked.');
        setIsSubmitting(false);
        return;
      }

      if (Date.now() - formLoadTime < 3000) {
        setSubmitStatus('error');
        setErrorMessage('Please take a moment to fill out the form.');
        setIsSubmitting(false);
        return;
      }

      const contactData: ContactFormData = {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        teamSize: formData.teamSize,
        subject: formData.subject,
        message: formData.message,
        inquiryType: formData.inquiryType,
        formLoadTime,
      };

      const result = await submitContactForm(contactData);
      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          teamSize: '',
          subject: '',
          message: '',
          inquiryType: 'general',
          honeypot: '',
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.message || 'Failed to send your message. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pb-20 pt-28">
      <div className="container-width section-padding">
        <p className="eyebrow">Contact</p>
        <h1 className="max-w-3xl font-display text-4xl sm:text-5xl">Write to the people who run SynqTag.</h1>
        <p className="lede mt-5">
          Email <a className="font-semibold text-forest" href="mailto:team@synqtag.com">team@synqtag.com</a> or
          use the form. There is no phone line and no live chat on this site.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
          <aside className="space-y-4">
            <div className="surface-card p-5">
              <h2 className="font-display text-xl">Email</h2>
              <p className="mt-2 text-sm text-ink-soft">For a short question, mail is the direct path.</p>
              <a href="mailto:team@synqtag.com" className="mt-3 inline-flex min-h-11 items-center font-semibold text-forest">
                team@synqtag.com
              </a>
            </div>
            <div className="surface-card p-5">
              <h2 className="font-display text-xl">What to expect</h2>
              <p className="mt-2 text-sm text-ink-soft">
                The form posts to the site’s contact endpoint. If it fails, the error stays on this
                page and you still have the email address.
              </p>
            </div>
          </aside>

          <div className="surface-card p-6 sm:p-8">
            {submitStatus === 'success' ? (
              <div role="status">
                <h2 className="font-display text-2xl">Message sent.</h2>
                <p className="mt-3 text-ink-soft">We have it. If you need to add something, send another note.</p>
                <button type="button" className="btn-premium mt-6" onClick={() => setSubmitStatus('idle')}>
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="relative space-y-5">
                {submitStatus === 'error' && (
                  <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {errorMessage || 'The form could not be sent.'} You can also email team@synqtag.com.
                  </div>
                )}

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="text-sm font-medium">Full name</label>
                    <input id="name" name="name" required autoComplete="name" value={formData.name} onChange={handleInputChange} className={fieldClass} />
                  </div>
                  <div>
                    <label htmlFor="email" className="text-sm font-medium">Email</label>
                    <input id="email" name="email" type="email" required autoComplete="email" value={formData.email} onChange={handleInputChange} className={fieldClass} />
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="company" className="text-sm font-medium">Company <span className="font-normal text-ink-soft">(optional)</span></label>
                    <input id="company" name="company" autoComplete="organization" value={formData.company} onChange={handleInputChange} className={fieldClass} />
                  </div>
                  <div>
                    <label htmlFor="teamSize" className="text-sm font-medium">Team size <span className="font-normal text-ink-soft">(optional)</span></label>
                    <select id="teamSize" name="teamSize" value={formData.teamSize} onChange={handleInputChange} className={fieldClass}>
                      <option value="">Select</option>
                      <option value="1-5">1–5 people</option>
                      <option value="6-20">6–20 people</option>
                      <option value="21-50">21–50 people</option>
                      <option value="51-200">51–200 people</option>
                      <option value="200+">200+ people</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="inquiryType" className="text-sm font-medium">Inquiry</label>
                  <select id="inquiryType" name="inquiryType" value={formData.inquiryType} onChange={handleInputChange} className={fieldClass}>
                    <option value="general">General</option>
                    <option value="sales">Pricing</option>
                    <option value="demo">A look at the product</option>
                    <option value="support">Support</option>
                    <option value="partnership">Partnership</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="text-sm font-medium">Subject</label>
                  <input id="subject" name="subject" required minLength={5} value={formData.subject} onChange={handleInputChange} className={fieldClass} />
                  <p className="mt-1 text-xs text-ink-soft">At least 5 characters.</p>
                </div>

                <div>
                  <label htmlFor="message" className="text-sm font-medium">Message</label>
                  <textarea id="message" name="message" required minLength={10} rows={6} value={formData.message} onChange={handleInputChange} className={`${fieldClass} resize-y`} />
                  <p className="mt-1 text-xs text-ink-soft">At least 10 characters.</p>
                </div>

                <div className="absolute h-px w-px overflow-hidden opacity-0" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input id="website" name="honeypot" tabIndex={-1} autoComplete="off" value={formData.honeypot} onChange={handleInputChange} />
                </div>

                <button type="submit" className="btn-premium" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
